import { generateClient, isCancelError, post, put } from "aws-amplify/api";
import { getCurrentUser } from "aws-amplify/auth";
import { getUrl } from "aws-amplify/storage";
import dayjs from "dayjs";
import { extractS3Key } from "../../Utils";

const client = generateClient();

export const requestGetConsultation = async (dispatch, payload) => {
  const { id, silent = false } = payload;
  try {
    dispatch({
      type: "REQUEST_GET_CONSULTATION",
      payload: { request: null },
    });

    const params = {
      id,
    };

    const request = client.graphql({
      query: getConsultationRequest,
      variables: params,
    });
    dispatch({ type: "REQUEST_GET_CONSULTATION", payload: { request } });

    const res = await request;

    const consultation = res.data.getConsultationRequest;

    if (!consultation) throw new Error("Consultation not found.");

    let description = {};

    try {
      description = JSON.parse(consultation.description);
    } catch (error) {
      description = {};
    }

    consultation.description = description;

    dispatch({
      type: "SUCCESS_GET_CONSULTATION",
      payload: { consultation },
    });
  } catch (error) {
    if (silent) {
      dispatch({ type: "ERROR_GET_CONSULTATION_SILENT" });
    } else {
      if (client.isCancelError(error)) {
        dispatch({
          type: "CANCEL_GET_CONSULTATION",
        });
      } else {
        dispatch({ type: "ERROR_GET_CONSULTATION", error });
      }
    }
  }
};

// Refetch the consultation WITHOUT dispatching REQUEST_GET_CONSULTATION, which
// sets loading and wipes consultation/messages — unusable mid-call. Errors are
// silent; callers fall back to whatever consultation state they already have.
export const requestRefreshConsultation = async (dispatch, payload) => {
  const { id } = payload;
  try {
    const res = await client.graphql({
      query: getConsultationRequest,
      variables: { id },
    });

    const consultation = res.data.getConsultationRequest;

    if (!consultation) return;

    try {
      consultation.description = JSON.parse(consultation.description);
    } catch (error) {
      consultation.description = {};
    }

    dispatch({
      type: "SUCCESS_GET_CONSULTATION",
      payload: { consultation },
    });
  } catch (error) {
    // silent — countdown keeps its local anchor
  }
};

export async function requestListConsultation(dispatch, payload) {
  try {
    const { status, page, limit, updatedAt, nextToken = null } = payload;

    if (page > 1) {
      dispatch({ type: "REQUEST_MORE_CONSULTATIONS" });
    } else {
      dispatch({ type: "REQUEST_LIST_CONSULTATION", payload: { page } });
    }

    const params = {
      status,
      sortDirection: "DESC",
      limit,
      updatedAt,
      nextToken,
    };

    const res = await client.graphql({
      query: consultationsByStatusUpdatedAt,
      variables: params,
    });

    let { items, nextToken: token } = res.data.consultationsByStatusUpdatedAt;
    const nextPage = page + 1;

    items = items.map((item) => {
      let description = {};
      try {
        description = JSON.parse(item.description);
      } catch (error) {
        description = {};
      }
      return { ...item, description };
    });

    if (items.length === 0) {
      token = null;
    }

    dispatch({
      type: "SUCCESS_LIST_CONSULTATION",
      payload: { items, nextToken: token, nextPage, append: page > 1 },
    });
  } catch (error) {
    dispatch({ type: "ERROR_LIST_CONSULTATION", error });
  }
}

export async function requestListConsultationByUser(dispatch, payload) {
  try {
    const { userID, page, limit, updatedAt, nextToken = null } = payload;

    if (page > 1) {
      dispatch({ type: "REQUEST_MORE_CONSULTATIONS" });
    } else {
      dispatch({ type: "REQUEST_LIST_CONSULTATION", payload: { page } });
    }

    const params = {
      userID,
      sortDirection: "DESC",
      limit,
      updatedAt,
      nextToken,
    };

    const res = await client.graphql({
      query: consultationsByUserUpdatedAt,
      variables: params,
    });

    let { items, nextToken: token } = res.data.consultationsByUserUpdatedAt;
    const nextPage = page + 1;

    items = items.map((item) => {
      let description = {};
      try {
        description = JSON.parse(item.description);
      } catch (error) {
        description = {};
      }
      return { ...item, description };
    });

    if (items.length === 0) {
      token = null;
    }

    dispatch({
      type: "SUCCESS_LIST_CONSULTATION",
      payload: { items, nextToken: token, nextPage, append: page > 1 },
    });
  } catch (error) {
    dispatch({ type: "ERROR_LIST_CONSULTATION", error });
  }
}

// A user may hold at most one ONGOING consultation that draws on the global
// call balance (checkout enforces this server-side); the form uses this to
// warn before the user reaches checkout. Plain fetch, no dispatch — must not
// clobber the consultation list state used by the Consultation page.
export async function checkOngoingGlobalConsultation(userID) {
  let nextToken = null;
  do {
    const res = await client.graphql({
      query: ongoingGlobalConsultationByUser,
      variables: {
        userID,
        sortDirection: "DESC",
        limit: 50,
        nextToken,
        filter: {
          status: { eq: "ONGOING" },
          useGlobalBalance: { eq: true },
        },
      },
    });
    const { items, nextToken: token } = res.data.consultationsByUserUpdatedAt;
    if (items.length > 0) return items[0];
    nextToken = token;
  } while (nextToken);
  return null;
}

export async function requestListConsultationByConsultant(dispatch, payload) {
  try {
    const {
      consultantOwner,
      page,
      limit,
      updatedAt,
      nextToken = null,
    } = payload;

    if (page > 1) {
      dispatch({ type: "REQUEST_MORE_CONSULTATIONS" });
    } else {
      dispatch({ type: "REQUEST_LIST_CONSULTATION", payload: { page } });
    }

    const params = {
      consultantOwner,
      sortDirection: "DESC",
      limit,
      updatedAt,
      nextToken,
    };

    const res = await client.graphql({
      query: consultationsByConsultantUpdatedAt,
      variables: params,
    });

    let { items, nextToken: token } =
      res.data.consultationsByConsultantUpdatedAt;
    const nextPage = page + 1;

    items = items.map((item) => {
      let description = {};
      try {
        description = JSON.parse(item.description);
      } catch (error) {
        description = {};
      }
      return { ...item, description };
    });

    if (items.length === 0) {
      token = null;
    }

    dispatch({
      type: "SUCCESS_LIST_CONSULTATION",
      payload: { items, nextToken: token, nextPage, append: page > 1 },
    });
  } catch (error) {
    dispatch({ type: "ERROR_LIST_CONSULTATION", error });
  }
}

export async function requestListOngoingConsultationByConsultant(
  dispatch,
  payload,
) {
  try {
    const { consultantOwner } = payload;
    dispatch({ type: "REQUEST_ONGOING_CONSULTATIONS" });

    // The GSI applies the status filter after the page limit, so paginate
    // until exhausted (capped) to collect every ONGOING consultation.
    let items = [];
    let nextToken = null;
    let pages = 0;
    do {
      const res = await client.graphql({
        query: consultationsByConsultantUpdatedAt,
        variables: {
          consultantOwner,
          sortDirection: "DESC",
          limit: 100,
          nextToken,
          filter: { status: { eq: "ONGOING" } },
        },
      });
      const data = res.data.consultationsByConsultantUpdatedAt;
      items = [...items, ...data.items];
      nextToken = data.nextToken;
      pages += 1;
    } while (nextToken && pages < 5);

    items = items.map((item) => {
      let description = {};
      try {
        description = JSON.parse(item.description);
      } catch (error) {
        description = {};
      }
      return { ...item, description };
    });

    dispatch({ type: "SUCCESS_ONGOING_CONSULTATIONS", payload: { items } });
  } catch (error) {
    dispatch({ type: "ERROR_ONGOING_CONSULTATIONS", error });
  }
}

export function clearOngoingConsultations(dispatch) {
  dispatch({ type: "CLEAR_ONGOING_CONSULTATIONS" });
}

export async function resetConsultationList(dispatch) {
  dispatch({ type: "RESET_CONSULTATIONS" });
}

export async function resetConsultationListError(dispatch) {
  dispatch({ type: "RESET_ERROR_CONSULTATION" });
}

export async function requestListMessage(dispatch, payload) {
  const { id, silent = false } = payload;
  try {
    if (silent) {
      dispatch({ type: "REQUEST_LIST_MESSAGE_SILENT" });
    } else {
      dispatch({
        type: "REQUEST_LIST_MESSAGE",
        payload: { listMessageRequest: null },
      });
    }

    const params = {
      consultationID: id,
      sortDirection: "DESC",
      limit: 10,
    };

    const request = client.graphql({
      query: messagesByConsultationCreatedAt,
      variables: params,
    });

    if (silent) {
      dispatch({ type: "REQUEST_LIST_MESSAGE_SILENT" });
    } else {
      dispatch({
        type: "REQUEST_LIST_MESSAGE",
        payload: { listMessageRequest: request },
      });
    }

    const res = await request;

    let { items, nextToken } = res.data.messagesByConsultationCreatedAt;

    let fetchedUrl = {};

    items = await Promise.all(
      items.map(async (item) => {
        let updatedItem = item;
        if (item.image?.key) {
          const message = item;
          let { key } = message.image;
          // Temporary function to fix uploaded path as key
          key = extractS3Key(key);
          try {
            let signedUrl = "";
            if (fetchedUrl[key]) {
              signedUrl = fetchedUrl[key];
            } else {
              const urlResult = await getUrl({
                path: ({ identityId }) => `private/${identityId}/${key}`,
                options: {
                  useAccelerateEndpoint: true,
                },
              });
              signedUrl = urlResult.url.href;
              fetchedUrl[key] = signedUrl;
            }
            if (signedUrl) {
              updatedItem = {
                ...updatedItem,
                imageUrl: signedUrl,
              };
            }
          } catch (error) {}
        }

        if (updatedItem.replyTo) {
          try {
            updatedItem.replyTo = JSON.parse(updatedItem.replyTo);
          } catch (error) {}
        }

        if (updatedItem.replyTo?.image?.key) {
          const message = updatedItem.replyTo;
          let { key } = message.image;
          // Temporary function to fix uploaded path as key
          key = extractS3Key(key);
          try {
            let signedUrl = "";
            if (fetchedUrl[key]) {
              signedUrl = fetchedUrl[key];
            } else {
              const urlResult = await getUrl({
                path: ({ identityId }) => `private/${identityId}/${key}`,
                options: {
                  useAccelerateEndpoint: true,
                },
              });
              signedUrl = urlResult.url.href;
              fetchedUrl[key] = signedUrl;
            }
            if (signedUrl) {
              updatedItem = {
                ...updatedItem,
                replyTo: {
                  ...updatedItem.replyTo,
                  imageUrl: signedUrl,
                },
              };
            }
          } catch (error) {}
        }

        if (updatedItem.action?.params) {
          try {
            updatedItem.action = {
              ...updatedItem.action,
              params: JSON.parse(updatedItem.action.params),
            };
          } catch (error) {}
        }

        return updatedItem;
      }),
    );

    items.reverse();

    let lastDate = null;
    items = items.map((item, index) => {
      const messageDate = dayjs(item.createdAt);
      item.date = messageDate.format("DD MMM YYYY");
      if (index === 0) {
        item.showDate = true;
        lastDate = messageDate;
      } else if (!messageDate.isSame(lastDate, "day")) {
        item.showDate = true;
        lastDate = messageDate;
      } else {
        item.showDate = false;
      }
      return item;
    });

    if (silent) {
      dispatch({
        type: "SUCCESS_LIST_MESSAGE_SILENT",
        payload: { items, nextToken },
      });
    } else {
      dispatch({
        type: "SUCCESS_LIST_MESSAGE",
        payload: { items, nextToken },
      });
    }
  } catch (error) {
    if (silent) {
      dispatch({ type: "ERROR_LIST_MESSAGE_SILENT" });
    } else {
      if (client.isCancelError(error)) {
        dispatch({
          type: "CANCEL_LIST_MESSAGE",
        });
      } else {
        dispatch({ type: "ERROR_LIST_MESSAGE", error });
      }
    }
  }
}

export async function requestListNextMessage(dispatch, payload) {
  const { id, nextToken: token } = payload;
  try {
    dispatch({ type: "REQUEST_LIST_NEXT_MESSAGE" });

    const params = {
      consultationID: id,
      sortDirection: "DESC",
      limit: 10,
      nextToken: token,
    };

    const res = await client.graphql({
      query: messagesByConsultationCreatedAt,
      variables: params,
    });

    let { items, nextToken } = res.data.messagesByConsultationCreatedAt;

    let fetchedUrl = {};

    items = await Promise.all(
      items.map(async (item) => {
        let updatedItem = item;
        if (item.image?.key) {
          const message = item;
          let { key } = message.image;
          // Temporary function to fix uploaded path as key
          key = extractS3Key(key);
          try {
            let signedUrl = "";
            if (fetchedUrl[key]) {
              signedUrl = fetchedUrl[key];
            } else {
              const urlResult = await getUrl({
                path: ({ identityId }) => `private/${identityId}/${key}`,
                options: {
                  useAccelerateEndpoint: true,
                },
              });
              signedUrl = urlResult.url.href;
              fetchedUrl[key] = signedUrl;
            }
            if (signedUrl) {
              updatedItem = {
                ...updatedItem,
                imageUrl: signedUrl,
              };
            }
          } catch (error) {}
        }

        if (updatedItem.replyTo) {
          try {
            updatedItem.replyTo = JSON.parse(updatedItem.replyTo);
          } catch (error) {}
        }

        if (updatedItem.replyTo?.image?.key) {
          const message = updatedItem.replyTo;
          let { key } = message.image;
          // Temporary function to fix uploaded path as key
          key = extractS3Key(key);
          try {
            let signedUrl = "";
            if (fetchedUrl[key]) {
              signedUrl = fetchedUrl[key];
            } else {
              const urlResult = await getUrl({
                path: ({ identityId }) => `private/${identityId}/${key}`,
                options: {
                  useAccelerateEndpoint: true,
                },
              });
              signedUrl = urlResult.url.href;
              fetchedUrl[key] = signedUrl;
            }
            if (signedUrl) {
              updatedItem = {
                ...updatedItem,
                replyTo: {
                  ...updatedItem.replyTo,
                  imageUrl: signedUrl,
                },
              };
            }
          } catch (error) {}
        }

        if (updatedItem.action?.params) {
          try {
            updatedItem.action = {
              ...updatedItem.action,
              params: JSON.parse(updatedItem.action.params),
            };
          } catch (error) {}
        }

        return updatedItem;
      }),
    );

    items.reverse();

    let lastDate = null;
    items = items.map((item, index) => {
      const messageDate = dayjs(item.createdAt);
      item.date = messageDate.format("DD MMM YYYY");
      if (index === 0) {
        item.showDate = true;
        lastDate = messageDate;
      } else if (!messageDate.isSame(lastDate, "day")) {
        item.showDate = true;
        lastDate = messageDate;
      } else {
        item.showDate = false;
      }
      return item;
    });

    dispatch({
      type: "SUCCESS_LIST_NEXT_MESSAGE",
      payload: { items, nextToken },
    });
  } catch (error) {
    dispatch({ type: "ERROR_LIST_NEXT_MESSAGE", error });
  }
}

export async function sendMessage(dispatch, payload) {
  try {
    dispatch({ type: "SEND_MESSAGE" });

    const {
      id,
      userID,
      text = null,
      image = null,
      document = null,
      replyTo = null,
      action = null,
    } = payload;

    const params = {
      input: {
        consultationID: id,
        userID,
      },
    };

    if (text) {
      params.input.text = text;
    }

    if (image) {
      params.input.image = image;
    }

    if (document) {
      params.input.document = document;
    }

    if (replyTo) {
      params.input.replyTo = replyTo;
    }

    if (action) {
      params.input.action = action;
    }

    const res = await client.graphql({
      query: createMessage,
      variables: params,
    });

    let message = res.data.createMessage;

    if (message.image?.key) {
      let { key } = message.image;
      // Temporary function to fix uploaded path as key
      key = extractS3Key(key);
      try {
        const urlResult = await getUrl({
          path: ({ identityId }) => `private/${identityId}/${key}`,
          options: {
            useAccelerateEndpoint: true,
          },
        });
        const signedUrl = urlResult.url.href;
        message = {
          ...message,
          imageUrl: signedUrl,
        };
      } catch (error) {}
    }

    if (message.replyTo) {
      try {
        message.replyTo = JSON.parse(message.replyTo);
        if (message.replyTo.image?.key) {
          let { key } = message.replyTo.image;
          // Temporary function to fix uploaded path as key
          key = extractS3Key(key);
          try {
            const urlResult = await getUrl({
              path: ({ identityId }) => `private/${identityId}/${key}`,
              options: {
                useAccelerateEndpoint: true,
              },
            });
            const signedUrl = urlResult.url.href;
            message.replyTo = {
              ...message.replyTo,
              imageUrl: signedUrl,
            };
          } catch (error) {}
        }
      } catch (error) {}
    }

    if (message.action?.params) {
      try {
        message.action = {
          ...message.action,
          params: JSON.parse(message.action.params),
        };
      } catch (error) {}
    }

    dispatch({
      type: "SUCCESS_SEND_MESSAGE",
      payload: { id, message },
    });
  } catch (error) {
    dispatch({ type: "ERROR_SEND_MESSAGE", error });
  }
}

export async function editMessage(dispatch, payload) {
  try {
    dispatch({ type: "EDIT_MESSAGE", payload: { request: null } });

    const { id, text } = payload;

    const params = {
      text,
    };

    const request = put({
      apiName: "consultation",
      path: `/message/${id}`,
      options: {
        body: params,
      },
    });

    dispatch({ type: "EDIT_MESSAGE", payload: { request } });

    const { body } = await request.response;
    const res = await body.json();

    const message = res.data;

    dispatch({
      type: "EDIT_MESSAGE_SUCCESS",
      payload: { message, consultationID: message.consultationID },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({ type: "EDIT_MESSAGE_CANCEL" });
    } else {
      dispatch({ type: "EDIT_MESSAGE_ERROR", error });
    }
  }
}

export async function newFirebaseMessage(dispatch, payload) {
  try {
    const { data } = payload;
    let { id, item } = data;

    item = JSON.parse(item);

    dispatch({ type: "INCOMING_MESSAGE", payload: { id } });

    const params = {
      id: item.id,
    };

    const res = await client.graphql({
      query: getMessage,
      variables: params,
    });

    let message = res.data.getMessage;

    if (message.image?.key) {
      let { key } = message.image;
      // Temporary function to fix uploaded path as key
      key = extractS3Key(key);
      try {
        const urlResult = await getUrl({
          path: ({ identityId }) => `private/${identityId}/${key}`,
          options: {
            useAccelerateEndpoint: true,
          },
        });
        const signedUrl = urlResult.url.href;
        message = {
          ...message,
          imageUrl: signedUrl,
        };
      } catch (error) {}
    }

    if (message.replyTo) {
      try {
        message.replyTo = JSON.parse(message.replyTo);
        if (message.replyTo.image?.key) {
          let { key } = message.replyTo.image;
          // Temporary function to fix uploaded path as key
          key = extractS3Key(key);
          try {
            const urlResult = await getUrl({
              path: ({ identityId }) => `private/${identityId}/${key}`,
              options: {
                useAccelerateEndpoint: true,
              },
            });
            const signedUrl = urlResult.url.href;
            message.replyTo = {
              ...message.replyTo,
              imageUrl: signedUrl,
            };
          } catch (error) {}
        }
      } catch (error) {}
    }

    if (message.action?.params) {
      try {
        message.action = {
          ...message.action,
          params: JSON.parse(message.action.params),
        };
      } catch (error) {}
    }

    dispatch({
      type: "SUCCESS_INCOMING_MESSAGE",
      payload: { id, message },
    });
  } catch (error) {
    dispatch({ type: "ERROR_INCOMING_MESSAGE", error });
  }
}

export async function updateFirebaseMessage(dispatch, payload) {
  try {
    const { data } = payload;
    if (!data?.id || !data?.item) {
      throw new Error("Invalid message data");
    }

    const updatedMessage = JSON.parse(data.item);

    dispatch({
      type: "UPDATE_FIREBASE_MESSAGE",
      payload: {
        consultationID: data.id,
        message: updatedMessage,
      },
    });
  } catch (error) {
    dispatch({
      type: "UPDATE_FIREBASE_MESSAGE_ERROR",
      error: error.message,
    });
  }
}

export function setOngoingCall(dispatch, payload) {
  dispatch({ type: "SET_ONGOING_CALL", payload });
}

export function setCallDialog(dispatch, payload) {
  dispatch({ type: "SET_CALL_DIALOG", payload });
}

export async function requestListService(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_LIST_SERVICE" });

    let guest = false;
    try {
      const user = await getCurrentUser();
      if (user.username) {
        guest = false;
      }
    } catch (error) {
      guest = true;
    }

    const params = {
      query: listServices,
      variables: { limit: 25 },
    };

    if (guest) {
      params.authMode = "iam";
    }

    const res = await client.graphql(params);

    let { items, nextToken } = res.data.listServices;

    items.sort(function (a, b) {
      var nameA = a.name.toUpperCase(); // ignore upper and lowercase
      var nameB = b.name.toUpperCase(); // ignore upper and lowercase
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      // names must be equal
      return 0;
    });

    dispatch({
      type: "SUCCESS_LIST_SERVICE",
      payload: { items, nextToken },
    });
  } catch (error) {
    dispatch({ type: "ERROR_LIST_SERVICE", error });
  }
}

export async function requestUpdateConsultation(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_UPDATE_CONSULTATION",
      payload: { request: null },
    });

    const { input } = payload;

    const request = client.graphql({
      query: updateConsultationRequest,
      variables: { input },
    });
    dispatch({ type: "REQUEST_UPDATE_CONSULTATION", payload: { request } });

    const res = await request;
    const updatedConsultation = res.data.updateConsultationRequest;
    const consultationID = res.data.updateConsultationRequest.id;

    dispatch({
      type: "UPDATE_CONSULTATION_SUCCESS",
      payload: { consultation: updatedConsultation, consultationID },
    });
  } catch (error) {
    dispatch({ type: "UPDATE_CONSULTATION_ERROR", error });
  }
}

export async function clearEditMessage(dispatch) {
  dispatch({ type: "CLEAR_EDIT_MESSAGE" });
}

export async function clearSendDraftStatus(dispatch) {
  dispatch({ type: "CLEAR_SEND_DRAFT_STATUS" });
}

export async function clearUpdateConsultation(dispatch) {
  dispatch({ type: "CLEAR_UPDATE_CONSULTATION" });
}

export async function clearUpdateConsultationError(dispatch) {
  dispatch({ type: "CLEAR_UPDATE_CONSULTATION_ERROR" });
}

export async function requestGetZoomToken(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_GET_ZOOM_TOKEN", payload: { request: null } });

    const { consultationId } = payload;

    const request = post({
      apiName: "consultation",
      path: "/zoom/token",
      options: {
        body: { consultation_id: consultationId },
      },
    });

    dispatch({ type: "REQUEST_GET_ZOOM_TOKEN", payload: { request } });

    const { body } = await request.response;
    const res = await body.json();

    if (!res.success) {
      throw new Error(res.message || "Failed to get Zoom token.");
    }

    dispatch({
      type: "SUCCESS_GET_ZOOM_TOKEN",
      payload: { zoomTokenData: res.data },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({ type: "ERROR_GET_ZOOM_TOKEN" });
    } else {
      dispatch({ type: "ERROR_GET_ZOOM_TOKEN", error });
    }
  }
}

export async function requestGetRtkToken(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_GET_RTK_TOKEN", payload: { request: null } });

    const { consultationId } = payload;

    const request = post({
      apiName: "consultation",
      path: "/rtk/token",
      options: {
        body: { consultation_id: consultationId },
      },
    });

    dispatch({ type: "REQUEST_GET_RTK_TOKEN", payload: { request } });

    const { body } = await request.response;
    const res = await body.json();

    if (!res.success) {
      throw new Error(res.message || "Failed to get RealtimeKit token.");
    }

    dispatch({
      type: "SUCCESS_GET_RTK_TOKEN",
      payload: { rtkTokenData: res.data },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({ type: "ERROR_GET_RTK_TOKEN" });
    } else {
      dispatch({ type: "ERROR_GET_RTK_TOKEN", error });
    }
  }
}

export function clearRtkToken(dispatch) {
  dispatch({ type: "CLEAR_RTK_TOKEN" });
}

const getConsultationRequest = /* GraphQL */ `
  query GetConsultationRequest($id: ID!) {
    getConsultationRequest(id: $id) {
      id
      status
      online
      description
      callEnabled
      callBalance
      useGlobalBalance
      activeCallStartedAt
      videoCredentials {
        sessionID
        token
      }
      videoProvider
      callType
      videoSession {
        provider
        meetingID
        topic
        expiresAt
      }
      zoomCredentials {
        topic
        meetingID
        url
      }
      zoomSdkCredentials {
        sessionName
        sessionKey
      }
      expiredAt
      updatedAt
      rating
      review
      createdAt
      scheduleID
      schedule {
        id
        program {
          id
          title
          duration
        }
        status
        scheduledAt
      }
      businessServiceOrder {
        id
        status
        detail {
          name
          email
          phone
          companyName
        }
        cityID
        city {
          id
          name
          province {
            id
            name
          }
        }
        businessService {
          id
          status
          categoryID
          consultantID
          price
          basePrice
          name
          description
          benefits
          serviceAreas {
            items {
              id
              city {
                id
                name
                province {
                  id
                  name
                }
              }
              createdAt
              updatedAt
            }
            nextToken
          }
          createdAt
          updatedAt
        }
        addOns {
          items {
            id
            addOn {
              id
              name
              description
              price
              basePrice
              createdAt
              updatedAt
            }
            createdAt
            updatedAt
          }
          nextToken
        }
        createdAt
        updatedAt
      }
      consultant {
        id
        acceptOnline
        active
        acceptCall
        acceptMeeting
        acceptVoucher
        startTimes
        endTimes
        activeDays
        user {
          id
          name
        }
        experience
        workStartYear
        consultationPrice
        callPrice
        audioPrice
        lastSeen
        totalRating
        rating
        totalMonthlyConsultation
        totalConsultation
        averageResponse
      }
      category {
        id
        name
        createdAt
        updatedAt
      }
      messages {
        nextToken
      }
      complaint {
        id
        consultationID
        title
        description
        status
        resolution
        refundID
        createdAt
        updatedAt
      }
    }
  }
`;

const consultationsByStatusUpdatedAt = /* GraphQL */ `
  query ConsultationsByStatusUpdatedAt(
    $status: ConsultationStatus
    $updatedAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelConsultationRequestFilterInput
    $limit: Int
    $nextToken: String
  ) {
    consultationsByStatusUpdatedAt(
      status: $status
      updatedAt: $updatedAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        status
        online
        description
        callEnabled
        callBalance
        useGlobalBalance
        videoCredentials {
          sessionID
          token
        }
        videoProvider
        callType
        videoSession {
          provider
          meetingID
          topic
          expiresAt
        }
        zoomSdkCredentials {
          sessionName
        }
        expiredAt
        updatedAt
        rating
        review
        createdAt
        consultant {
          id
          acceptOnline
          active
          acceptCall
          acceptMeeting
          acceptVoucher
          startTimes
          endTimes
          activeDays
          user {
            id
            name
          }
          experience
          workStartYear
          consultationPrice
          callPrice
          audioPrice
          lastSeen
          totalRating
          rating
          totalMonthlyConsultation
          totalConsultation
          averageResponse
        }
        category {
          id
          name
          createdAt
          updatedAt
        }
        messages {
          nextToken
        }
        complaint {
          id
          consultationID
          title
          description
          status
          resolution
          refundID
          createdAt
          updatedAt
        }
      }
      nextToken
    }
  }
`;

const messagesByConsultationCreatedAt = /* GraphQL */ `
  query MessagesByConsultationCreatedAt(
    $consultationID: ID
    $createdAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelMessageFilterInput
    $limit: Int
    $nextToken: String
  ) {
    messagesByConsultationCreatedAt(
      consultationID: $consultationID
      createdAt: $createdAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        text
        action {
          target
          name
          params
        }
        image {
          bucket
          region
          key
        }
        document {
          bucket
          region
          key
        }
        replyTo
        system
        deliveredAt
        readAt
        createdAt
        isEdited
        updatedAt
        user {
          id
          name
        }
      }
      nextToken
    }
  }
`;

const createMessage = /* GraphQL */ `
  mutation CreateMessage($input: CreateMessageInput!) {
    createMessage(input: $input) {
      id
      text
      action {
        target
        name
        params
      }
      image {
        bucket
        region
        key
      }
      document {
        bucket
        region
        key
      }
      replyTo
      system
      deliveredAt
      readAt
      createdAt
      isEdited
      updatedAt
      user {
        id
        name
      }
    }
  }
`;

const getMessage = /* GraphQL */ `
  query GetMessage($id: ID!) {
    getMessage(id: $id) {
      id
      text
      action {
        target
        name
        params
      }
      image {
        bucket
        region
        key
      }
      document {
        bucket
        region
        key
      }
      replyTo
      system
      deliveredAt
      readAt
      createdAt
      isEdited
      updatedAt
      user {
        id
        name
      }
    }
  }
`;

const listServices = /* GraphQL */ `
  query ListServices(
    $filter: ModelServiceFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listServices(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const consultationsByConsultantUpdatedAt = /* GraphQL */ `
  query ConsultationsByConsultantUpdatedAt(
    $consultantOwner: String
    $updatedAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelConsultationRequestFilterInput
    $limit: Int
    $nextToken: String
  ) {
    consultationsByConsultantUpdatedAt(
      consultantOwner: $consultantOwner
      updatedAt: $updatedAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        status
        online
        description
        callEnabled
        callBalance
        useGlobalBalance
        videoCredentials {
          sessionID
          token
        }
        videoProvider
        callType
        videoSession {
          provider
          meetingID
          topic
          expiresAt
        }
        zoomSdkCredentials {
          sessionName
        }
        expiredAt
        updatedAt
        rating
        review
        createdAt
        consultant {
          id
          acceptOnline
          active
          acceptCall
          acceptMeeting
          acceptVoucher
          startTimes
          endTimes
          activeDays
          user {
            id
            name
          }
          experience
          workStartYear
          consultationPrice
          callPrice
          audioPrice
          lastSeen
          totalRating
          rating
          totalMonthlyConsultation
          totalConsultation
          averageResponse
        }
        category {
          id
          name
          createdAt
          updatedAt
        }
        messages {
          nextToken
        }
        complaint {
          id
          consultationID
          title
          description
          status
          resolution
          refundID
          createdAt
          updatedAt
        }
      }
      nextToken
    }
  }
`;

// Minimal projection for checkOngoingGlobalConsultation. The GSI applies the
// filter after the page limit, so the caller paginates until a hit.
const ongoingGlobalConsultationByUser = /* GraphQL */ `
  query OngoingGlobalConsultationByUser(
    $userID: ID
    $sortDirection: ModelSortDirection
    $filter: ModelConsultationRequestFilterInput
    $limit: Int
    $nextToken: String
  ) {
    consultationsByUserUpdatedAt(
      userID: $userID
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        status
        useGlobalBalance
      }
      nextToken
    }
  }
`;

const consultationsByUserUpdatedAt = /* GraphQL */ `
  query ConsultationsByUserUpdatedAt(
    $userID: ID
    $updatedAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelConsultationRequestFilterInput
    $limit: Int
    $nextToken: String
  ) {
    consultationsByUserUpdatedAt(
      userID: $userID
      updatedAt: $updatedAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        status
        online
        description
        callEnabled
        callBalance
        useGlobalBalance
        videoCredentials {
          sessionID
          token
        }
        videoProvider
        callType
        videoSession {
          provider
          meetingID
          topic
          expiresAt
        }
        zoomSdkCredentials {
          sessionName
        }
        expiredAt
        updatedAt
        rating
        review
        createdAt
        consultant {
          id
          acceptOnline
          active
          acceptCall
          acceptMeeting
          acceptVoucher
          startTimes
          endTimes
          activeDays
          user {
            id
            name
          }
          experience
          workStartYear
          consultationPrice
          callPrice
          audioPrice
          lastSeen
          totalRating
          rating
          totalMonthlyConsultation
          totalConsultation
          averageResponse
        }
        category {
          id
          name
          createdAt
          updatedAt
        }
        messages {
          nextToken
        }
        complaint {
          id
          consultationID
          title
          description
          status
          resolution
          refundID
          createdAt
          updatedAt
        }
      }
      nextToken
    }
  }
`;

const updateConsultationRequest = /* GraphQL */ `
  mutation UpdateConsultationRequest($input: UpdateConsultationRequestInput!) {
    updateConsultationRequest(input: $input) {
      id
      status
      online
      description
      callEnabled
      callBalance
      useGlobalBalance
      videoCredentials {
        sessionID
        token
      }
      videoProvider
      callType
      videoSession {
        provider
        meetingID
        topic
        expiresAt
      }
      expiredAt
      updatedAt
      rating
      review
      createdAt
      consultant {
        id
        acceptOnline
        active
        acceptCall
        acceptMeeting
        acceptVoucher
        startTimes
        endTimes
        activeDays
        user {
          id
          name
        }
        experience
        workStartYear
        consultationPrice
        callPrice
        audioPrice
        lastSeen
        totalRating
        rating
        totalMonthlyConsultation
        totalConsultation
        averageResponse
      }
      category {
        id
        name
        createdAt
        updatedAt
      }
      messages {
        nextToken
      }
      complaint {
        id
        consultationID
        title
        description
        status
        resolution
        refundID
        createdAt
        updatedAt
      }
    }
  }
`;
